(() => {
  const GRID_SELECTOR = '[data-infinite-grid]';
  const PAGINATION_SELECTOR = '[data-infinite-pagination]';
  const SENTINEL_SELECTOR = '[data-infinite-sentinel]';
  const STATUS_SELECTOR = '[data-infinite-status]';

  class CollectionInfiniteScroll {
    constructor(root = document) {
      this.root = root;
      this.grid = root.querySelector(GRID_SELECTOR);
      this.pagination = root.querySelector(PAGINATION_SELECTOR);
      this.sentinel = root.querySelector(SENTINEL_SELECTOR);
      this.status = root.querySelector(STATUS_SELECTOR);

      this.loading = false;
      this.finished = false;
      this.controller = null;

      if (!this.grid || !this.pagination || !this.sentinel) return;

      this.sectionId = this.grid.dataset.sectionId;
      this.nextUrl = this.getNextUrl(this.pagination);

      if (!this.nextUrl) {
        this.finish();
        return;
      }

      this.hidePagination();
      this.observe();
    }

    getNextUrl(container) {
      const explicitNext = container.querySelector(
        'a[rel="next"], a[aria-label*="Next" i], a[href*="page="]'
      );

      if (!explicitNext) return null;

      /*
       * If the fallback matched a numbered pagination link, select the first
       * page link whose page number is greater than the current page.
       */
      const relNext = container.querySelector('a[rel="next"]');

      return new URL(
        (relNext || explicitNext).href,
        window.location.origin
      ).toString();
    }

    hidePagination() {
      this.pagination.hidden = true;
      this.pagination.setAttribute('aria-hidden', 'true');
    }

    observe() {
      this.observer = new IntersectionObserver(
        entries => {
          if (entries.some(entry => entry.isIntersecting)) {
            this.loadNextPage();
          }
        },
        {
          rootMargin: '800px 0px',
          threshold: 0
        }
      );

      this.observer.observe(this.sentinel);
    }

    async loadNextPage() {
      if (this.loading || this.finished || !this.nextUrl) return;

      this.loading = true;
      this.setLoadingState(true);

      this.controller?.abort();
      this.controller = new AbortController();

      try {
        const url = new URL(this.nextUrl);

        /*
         * Shopify returns only this section's rendered HTML.
         * Existing collection query parameters, including filters and sort
         * parameters, remain in nextUrl.
         */
        if (this.sectionId) {
          url.searchParams.set('section_id', this.sectionId);
        }

        const response = await fetch(url.toString(), {
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          },
          signal: this.controller.signal
        });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(
          html,
          'text/html'
        );

        const newGrid = documentFragment.querySelector(GRID_SELECTOR);
        const newPagination =
          documentFragment.querySelector(PAGINATION_SELECTOR);

        if (!newGrid) {
          throw new Error('The product grid was not found in the response.');
        }

        const newProducts = Array.from(newGrid.children);

        if (newProducts.length === 0) {
          this.finish();
          return;
        }

        const fragment = document.createDocumentFragment();

        newProducts.forEach(product => {
          fragment.appendChild(product);
        });

        this.grid.appendChild(fragment);
        newProducts.forEach(product => {
        product.classList.add('infinite-scroll-loaded');

        product
            .querySelectorAll('[reveal-on-scroll], .reveal, [style*="opacity"]')
            .forEach(element => {
            element.classList.add('infinite-scroll-loaded');
            element.style.opacity = '1';
            element.style.visibility = 'visible';
            element.style.transform = 'none';
            });
        });
        this.nextUrl = newPagination
          ? this.getNextUrl(newPagination)
          : null;

        this.dispatchProductsLoaded(newProducts);

        if (!this.nextUrl) {
          this.finish();
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('[Infinite scroll]', error);

          /*
           * Restore normal pagination as a fallback if loading fails.
           */
          this.pagination.hidden = false;
          this.pagination.removeAttribute('aria-hidden');
          this.observer?.disconnect();
        }
      } finally {
        this.loading = false;
        this.setLoadingState(false);
      }
    }

    dispatchProductsLoaded(products) {
      document.dispatchEvent(
        new CustomEvent('collection:products-loaded', {
          detail: {
            products,
            grid: this.grid
          }
        })
      );
    }

    setLoadingState(isLoading) {
      if (!this.status) return;

      this.status.hidden = !isLoading;
      this.sentinel.setAttribute(
        'aria-busy',
        isLoading ? 'true' : 'false'
      );
    }

    finish() {
      this.finished = true;
      this.nextUrl = null;
      this.observer?.disconnect();

      if (this.pagination) {
        this.pagination.hidden = true;
      }

      if (this.sentinel) {
        this.sentinel.hidden = true;
      }
    }

    destroy() {
      this.observer?.disconnect();
      this.controller?.abort();
    }
  }

  let instance;

  const initializeInfiniteScroll = () => {
    instance?.destroy();
    instance = new CollectionInfiniteScroll(document);
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeInfiniteScroll,
      { once: true }
    );
  } else {
    initializeInfiniteScroll();
  }

  /*
   * Prestige may replace the collection markup after filters or sorting.
   * Reinitialize when a new infinite-scroll grid appears.
   */
  const mutationObserver = new MutationObserver(mutations => {
    const collectionWasReplaced = mutations.some(mutation =>
      Array.from(mutation.addedNodes).some(node => {
        return (
          node.nodeType === Node.ELEMENT_NODE &&
          (node.matches?.(GRID_SELECTOR) ||
            node.querySelector?.(GRID_SELECTOR))
        );
      })
    );

    if (collectionWasReplaced) {
      window.requestAnimationFrame(initializeInfiniteScroll);
    }
  });

  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true
  });
})();